<template>
  <card-component
    truncate
    class="module-library-card"
    size="2xlarge"
    :aria-label="ariaLabelConfig?.cardComponent"
    :class="{ paused: disabled }"
    @on-click="handleClick"
    @keydown.enter="handleClick"
  >
    <template #header>
      <label-component v-if="gameEnabled" kind="gamified" :text="t('gamified_label')" />
    </template>

    <template #background>
      <img
        v-show="showThumbnail"
        :src="thumbnailUrl ?? undefined"
        class="thumbnail"
        @load="onImageLoaded"
      />
    </template>

    <template #content>
      <div class="content-heading">
        <!-- Class-group name (Self Study / Group Resources for CLASS_GROUP items) takes
             priority over the generic domain (MOE/Community/My Drive) label. -->
        <label-component v-if="groupLabel" kind="class-group" :text="groupLabel" />
        <label-component
          v-else
          :kind="labelConfig?.kind ?? 'domain'"
          :class="getLabelCssClass()"
          :text="getLocationLabel(resourceDomain)"
        />

        <div class="metadata">
          <li>
            <span v-if="passcodeEnabled">
              <svg-icon name="Passcode16" kind="indicative" />
              <cv-tooltip
                direction="bottom"
                alignment="end"
                :tip="tooltipConfig?.accessCode ?? t('module_card_access_code_tooltip')"
              >
              </cv-tooltip>
            </span>

            <span
              v-if="showResourceCount"
              class="view-count number-abbr-ui"
              role="img"
              :aria-label="ariaLabelConfig?.viewCount"
            >
              <svg-icon name="View16" kind="action" />
              {{ formattedViewCount(viewCount) }}
              <cv-tooltip
                direction="bottom"
                alignment="end"
                :tip="tooltipConfig?.viewCount ?? ariaLabelConfig?.viewCount"
              >
              </cv-tooltip>
            </span>
            <span v-if="showTeacherReviewCount" class="review-count">
              <svg-icon name="Review16" kind="action" />
              {{ props.teacherReviewCount }}
              <cv-tooltip
                direction="bottom"
                alignment="end"
                :tip="tooltipConfig?.teacherReviewCount ?? getTeacherReviewCountLabel()"
              >
              </cv-tooltip>
            </span>
          </li>
        </div>
      </div>

      <h6 class="content-title">{{ title }}</h6>
      <!-- Visible tooltip (not just aria-label) shown on hover for Paused resources. (only Student)-->
      <cv-tooltip
        v-if="disabled"
        alignment="center"
        direction="bottom"
        :tip="tooltipConfig?.paused ?? t('module_card_paused_tooltip')"
      />
    </template>

    <!-- Section-display pill. CardComponent's `.content-info` wrapper auto-hides when this
         slot renders nothing (`&:empty`), so guard the whole block with one v-if. -->
    <template v-if="sectionText || lastAccessedDisplay || completionStatus" #content-info>
      <div class="section-lines">
        <h6 v-if="sectionText" class="section-text">{{ sectionText }}</h6>
        <label-component
          v-if="completionStatus"
          kind="assignment-status"
          :class="completionStatus.toLowerCase()"
          :text="completionStatusLabel"
        />
      </div>
      <p
        v-if="lastAccessedDisplay"
        v-safe-html="t('module_card_last_accessed', { date: lastAccessedDisplay })"
        class="last-accessed"
      />
    </template>

    <template #actions>
      <slot name="actions">
        <cv-button
          v-if="!showMenuOptions"
          class="icon-button"
          kind="ghost"
          :aria-label="ariaLabelConfig?.viewDetails"
          tabindex="-1"
          @click="handleViewDetails"
        >
          <svg-icon name="Details24" kind="action" />
        </cv-button>

        <overflow-menu
          v-else
          class="menu-actions"
          :aria-label="ariaLabelConfig?.overflowMenu"
          :flip-menu="true"
          tabindex="-1"
        >
          <template #trigger>
            <svg-icon name="Kebab24" kind="action" />
          </template>

          <!-- Show Details -->
          <OverflowMenuItem :aria-label="ariaLabelConfig?.viewDetails" @click="handleViewDetails">
            <svg-icon name="Details24" kind="action" />
            {{ t("search_result_card_view_details_menu_label") }}
          </OverflowMenuItem>

          <!-- View Reviews -->
          <OverflowMenuItem v-if="showTeacherReview" @click="handleViewReviews">
            <svg-icon name="Review24" kind="action" />
            {{ t("search_result_card_view_reviews_menu_label") }}
          </OverflowMenuItem>

          <!-- Copy To -->
          <OverflowMenuItem
            v-if="showCopyTo"
            :disabled="!isResourceDuplicatable"
            @click="handleCopy"
          >
            <svg-icon name="Copy24" kind="action" />
            {{ t("search_result_card_copy_to_menu_label") }}
          </OverflowMenuItem>
        </overflow-menu>
      </slot>
    </template>
  </card-component>
</template>
<script setup lang="ts">
import CardComponent from "@plugins/card/components/CardComponent.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import OverflowMenu from "@plugins/common/components/OverflowMenu.vue";
import { ModuleCardProps } from "@/common/card-types.ts";
import { useI18n } from "vue-i18n";
import { computed, ref } from "vue";
import { isEmpty } from "@utils/var.check.ts";
import { RESOURCE_DOMAIN } from "@plugins/common/constant/resource.constant.ts";
import { getLocationLabel } from "@/modules/resource-search/resource-search-helper";
import { formattedViewCount } from "@modules/resource-browse/resource-browse-helper.ts";
import { resolveSectionDisplayText } from "@modules/pending-action/service/section-display.utils.ts";

const props = defineProps<ModuleCardProps>();

interface Emits {
  (event: "click"): void;
  (event: "view-details"): void;
  (event: "view-review"): void;
  (event: "copy"): void;
}
const emit = defineEmits<Emits>();
const { t } = useI18n();

const imgLoaded = ref(false);
const hasThumbnail = ref(!isEmpty(props.thumbnailUrl));

const showThumbnail = computed(() => {
  return hasThumbnail.value && imgLoaded.value;
});

const onImageLoaded = () => {
  imgLoaded.value = true;
};

const handleClick = () => {
  if (props.disabled) return;
  emit("click");
};

const handleCopy = () => {
  emit("copy");
};

const handleViewDetails = () => {
  emit("view-details");
};

const handleViewReviews = () => {
  emit("view-review");
};

const getTeacherReviewCountLabel = () => {
  return t("search_result_card_view_teacher_review_count_label", {
    count: props.teacherReviewCount,
  });
};

const getLabelCssClass = () => {
  return {
    moe: props.resourceDomain === RESOURCE_DOMAIN.MOE,
    community: props.resourceDomain === RESOURCE_DOMAIN.COMMUNITY,
  };
};

const sectionText = computed(() => {
  if (props.sectionDisplay) {
    return resolveSectionDisplayText(props.sectionDisplay, t);
  }
  return props.paused ? t("module_is_paused") : null;
});

const completionStatusLabel = computed(() => {
  switch (props.completionStatus) {
    case "COMPLETE":
      return t("module_card_status_complete");
    case "PAUSED":
      return t("module_card_status_paused");
    case "INCOMPLETE":
      return t("module_card_status_incomplete");
    default:
      return "";
  }
});
</script>
